import { fetchEventSource } from "@microsoft/fetch-event-source";
import { ChatRequestBodySchema, ChatEventHandlers } from "./chatSchema";
import { z } from "zod";
/**
 * Streams chat responses from the ZSL service, providing real-time updates
 * as the responses are generated.
 *
 * @param input - The input configuration for the chat request
 * @param input.messages - Array of chat messages
 * @param input.model - Optional model override to use
 * @param input.system - Optional system message override
 * @param input.tools - Optional tools configuration
 * @param input.baseUrl - Optional override for the API base URL
 *
 * @param handlers - Callback functions for handling the streaming response
 * @param handlers.onPartial - Called when partial results are available
 * @param handlers.onComplete - Called when complete results are available
 * @param handlers.onError - Called when an error occurs
 *
 * @param signal - Optional AbortSignal to cancel the stream
 *
 * @example
 * ```typescript
 * await streamChatResponse(
 *   {
 *     messages: [
 *       { role: "user", content: "Create a workflow for customer onboarding" }
 *     ],
 *   },
 *   {
 *     onPartial: (data) => {
 *       // Handle partial updates (e.g., show typing indicators)
 *       console.log('Partial update:', data);
 *     },
 *     onComplete: (data) => {
 *       // Handle complete updates
 *       console.log('Complete update:', data);
 *     },
 *     onError: (error) => {
 *       // Handle any errors
 *       console.error('Error:', error);
 *     }
 *   }
 * );
 * ```
 */
export async function streamChatResponse(
  input: z.infer<typeof ChatRequestBodySchema>,
  handlers: ChatEventHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const validatedInput = ChatRequestBodySchema.parse(input);

  const { baseUrl, ...chatInput } = validatedInput;

  let lastDataChunk: any;

  try {
    await fetchEventSource(`/api/zsl/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(baseUrl && { "z-override-base-url": baseUrl }),
      },
      body: JSON.stringify(chatInput),
      openWhenHidden: true,
      signal,
      onmessage(msg) {
        try {
          const data = JSON.parse(msg.data);

          if (data.error) {
            handlers.onError?.(data);
            return;
          }

          lastDataChunk = data;
          handlers.onPartial?.(lastDataChunk);
        } catch (error) {
          handlers.onError?.({
            error:
              error instanceof Error ? error.message : "Invalid message data",
          });
        }
      },
      onclose() {
        handlers.onComplete?.(lastDataChunk);
      },
      onerror(err) {
        handlers.onError?.({ error: err.message });
      },
    });
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return;
    }
    handlers.onError?.({
      error:
        error instanceof Error ? error.message : "Stream processing failed",
    });
  }
}
